import styled from '@emotion/styled';
import React from 'react';

import Link from '~/components/Link';
import { css, hover } from '~/css';
import { useFrontmatter } from '~/shared/contexts/frontmatter';
import type { FrontmatterNavigationItem } from '~/gamePageTypes';
import cn from '~/shared/utils/cn';
import useWatchingSection from '~/shared/contexts/watchingSection';
import { SkinTheme, skin } from '~/theme';
import { rem } from 'polished';

interface NavigationMenuProps {
  className?: string;
  variant: 'pc' | 'mobile';
  onMenuItemClick?: (item: FrontmatterNavigationItem) => void;
}

interface DirectionVariant {
  variant: 'horizontal' | 'vertical';
}

const Container = styled('nav')(
  skin({
    blackSquare: css({
      textAlign: 'center',
      fontWeight: 400,
      ':lang(ja)': {
        fontWeight: 700,
      },
      ':lang(zh-Hant)': {
        fontWeight: 700,
      },
      ':lang(th)': {
        fontWeight: 700,
      },
    }),
  }),
);

const MenuItemList = styled('ul')((props: DirectionVariant) =>
  skin({
    blackSquare: css({
      display: 'flex',
      listStyle: 'none',
      paddingLeft: 0,
      margin: 0,
      marginTop: [0, 30, 0],
      flexDirection: props.variant === 'horizontal' ? 'row' : 'column',
    })(props),
  }),
);

const MenuItem = styled('li')((props: DirectionVariant) =>
  skin({
    blackSquare: css({
      ...(props.variant === 'horizontal' ? { marginLeft: rem(32) } : { marginBottom: [30, 50] }),
    })(props),
  }),
);

const MenuLink = styled(Link)<NavigationMenuProps>((props) =>
  skin({
    blackSquare: (theme: SkinTheme<'blackSquare'>) =>
      css({
        fontWeight: 'navbar',
        textDecoration: 'none',
        whiteSpace: 'nowrap',
        ...(props.variant === 'pc' && {
          fontSize: 24,
          // 일본어 패치..
          ':lang(ja)': {
            fontSize: 19,
          },
          color: '#000',
          ...theme.underline('&:hover'),
        }),
        ...(props.variant !== 'pc' && {
          fontSize: [33, 42],
          color: '#fff',
          ...hover({
            color: 'secondary',
          }),
        }),
      })(props),
  }),
);

const NavigationMenu: React.FC<NavigationMenuProps> = ({ className, variant, onMenuItemClick }) => {
  const frontmatter = useFrontmatter();
  // 메뉴 아이템은 4개까지만 추가 가능합니다.
  const menuItems = frontmatter.navigation.items.slice(0, 4);
  const directionVariant = variant === 'pc' ? 'horizontal' : 'vertical';
  const { scrollToSection } = useWatchingSection();

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>, item: FrontmatterNavigationItem) => {
    if (item.url.startsWith('#')) {
      e.preventDefault();
      scrollToSection(item.url);
    }
    onMenuItemClick?.(item);
  };

  return (
    <Container className={cn('font-cookierun-bold', className)} role='navigation'>
      <MenuItemList variant={directionVariant}>
        {menuItems.map((item, index) => (
          <MenuItem key={index} variant={directionVariant}>
            <MenuLink onClick={(e) => handleClick(e, item)} variant={variant} className='gnb-link' to={item.url}>
              {item.label}
            </MenuLink>
          </MenuItem>
        ))}
      </MenuItemList>
    </Container>
  );
};

export default NavigationMenu;
